import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { 
  Box, 
  Typography, 
  Button, 
  Stack, 
  Divider,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  FileUpload as UploadIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import '../../css/users.css';

function UserList() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); // ✅ 검색어 상태 추가
  const [csvFile, setCsvFile] = useState(null);
  const [useMailEnabled, setUseMailEnabled] = useState(false);
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get('/api/auth/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
    } catch (err) {
      console.error('회원 목록 요청 실패:', err);
      alert('접근 권한이 없습니다. 다시 로그인해주세요.');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleDelete = async (id) => {
    try {
      if (window.confirm('정말 삭제하시겠습니까?')) {
        await axios.delete(`/api/auth/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchUsers();
      }
    } catch (err) {
      console.error('회원 삭제 실패:', err);
      alert(err.response?.data?.message || '삭제 중 오류 발생');
    }
  };

  const handleCreateMailbox = async (id) => {
    try {
      const res = await axios.post(`/api/mail/create/${id}`, {},
        { headers: { Authorization: `Bearer ${token}`, }, }
      );
      console.log(res.data);
      alert("메일 생성 완료");
    } catch (err) {
      console.error("메일 생성 오류:", err);
      alert("메일 생성 실패");
    }
  };

  const handleDeleteMailbox = async (id) => {
    try {
      if (window.confirm("정말 메일을 삭제하시겠습니까?")) {
        const res = await axios.delete(`/api/mail/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log(res.data);
        alert("메일 삭제 완료");
      }
    } catch (err) {
      console.error("메일 삭제 오류:", err);
      alert("메일 삭제 실패");
    }
  };

  const handleRegisterSso = async (userid) => {
    try {
      const res = await axios.post(
        `/api/auth/authentik/register-sso/${userid}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(res.data);
      alert("SSO 계정 생성 완료");
    } catch (err) {
      console.error("SSO 계정 생성 오류:", err);
      alert(err.response?.data?.message || "SSO 계정 생성 실패");
    }
  };


  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const M_ComCode = user?.M_ComCode;
    if (!M_ComCode) return;
    axios.get(`/api/managers/${M_ComCode}`)
      .then(res => setUseMailEnabled(res.data?.M_UseMail === 1 || res.data?.M_UseMail === true))
      .catch(err => console.error('manager 정보 조회 실패:', err));
  }, [user]);

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('CSV 파일을 선택하세요');
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        for (const row of result.data) {
          try {
            // userid 기준으로 존재 여부 확인
            const exists = users.find(u => u.userid === row.userid);

            if (exists) {
              // 수정
              await axios.put(`/api/auth/users/${exists.id}`, row, {
                headers: { Authorization: `Bearer ${token}` },
              });
              console.log(`✅ Updated: ${row.userid}`);
            } else {
              // 등록
              await axios.post('/api/auth/register', row, {
                headers: { Authorization: `Bearer ${token}` },
              });
              console.log(`➕ Created: ${row.userid}`);
            }
          } catch (err) {
            console.error('CSV 처리 오류:', err);
          }
        }
        fetchUsers(); // 목록 갱신
        alert('CSV 업로드 완료!');
      },
    });
  };


  // ✅ 검색어로 필터링된 사용자 목록
  const filteredUsers = users.filter(
    (u) =>
      u.userid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 헤더 영역 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 3,
            pl: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 5, height: 26, bgcolor: '#1976d2', borderRadius: 2 }} />
              <PeopleIcon sx={{ color: '#1976d2', fontSize: '1.7rem' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '1.3rem' }}>
                사용자 관리
              </Typography>
              <Box sx={{ 
                px: 1, 
                py: 0.1, 
                bgcolor: '#f1f3f5', 
                borderRadius: '4px', 
                fontSize: '0.65rem', 
                color: '#6c757d', 
                fontWeight: 'bold',
                border: '1px solid #dee2e6',
                ml: 0.5
              }}>
                USER MANAGEMENT
              </Box>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate('/users/create')}
                sx={{ height: 32, fontWeight: 'bold' }}
              >
                사용자 등록
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, my: 'auto' }} />

              {/* ✅ CSV 업로드 UI */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input
                  type="file"
                  id="csv-upload-input"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={(e) => setCsvFile(e.target.files[0])}
                />
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor="csv-upload-input"
                  size="small"
                  sx={{ height: 32, borderColor: '#dee2e6', color: '#495057' }}
                >
                  파일 선택
                </Button>
                <Box sx={{ 
                  minWidth: 150, 
                  height: 32, 
                  border: '1px solid #dee2e6', 
                  borderRadius: 1, 
                  px: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: '0.8rem',
                  color: csvFile ? '#212529' : '#adb5bd',
                  bgcolor: '#f8f9fa'
                }}>
                  {csvFile ? csvFile.name : '선택된 파일 없음'}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={handleCsvUpload}
                  sx={{ height: 32, bgcolor: '#4e73df', '&:hover': { bgcolor: '#2e59d9' } }}
                >
                  CSV 업로드
                </Button>
              </Box>

              <Button
                variant="contained"
                size="small"
                color="info"
                startIcon={<DownloadIcon />}
                href="/sampleFile/userSample.csv"
                download="userSample.csv"
                sx={{ height: 32, color: '#fff' }}
              >
                샘플 다운로드
              </Button>
              
              <IconButton 
                size="small" 
                onClick={fetchUsers}
                sx={{ border: '1px solid #dee2e6', borderRadius: 1, height: 32, width: 32 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* 검색 폼 */}
          <form
            className="row g-2 mb-3 users-search-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="col-md-6">
              <input
                type="text"
                name="search"
                className="form-control"
                placeholder="아이디 또는 이름 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                초기화
              </button>
            </div>
          </form>

          {/* 회원 목록 */}
          {filteredUsers.length === 0 ? (
            <div className="users-empty">
              <div className="spinner-border text-secondary" role="status" />
              <p>사용자가 없습니다.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-nowrap align-middle table-hover mb-0 users-table">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '10%' }}>ID</th>
                    <th style={{ width: '20%' }}>아이디</th>
                    <th style={{ width: '20%' }}>이름</th>
                    <th style={{ width: '20%' }}>권한</th>
                    <th style={{ width: '30%' }} className="text-center">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${u.id}`)}
                      >
                        <h5 className="font-size-14 mb-0">{u.userid}</h5>
                      </td>
                      <td>{u.name}</td>
                      <td>{u.role}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-warning btn-sm me-2"
                          onClick={() => navigate(`/users/edit/${u.id}`)}
                        >
                          수정
                        </button>
                        <button
                          className="btn btn-danger btn-sm me-2"
                          onClick={() => handleDelete(u.id)}
                        >
                          삭제
                        </button>
                        {/* ✅ M_UseMail이 활성화된 경우에만 메일 버튼 표시 */}
                        {useMailEnabled && u.maildb === 0 && (
                          <button
                            className="btn btn-info btn-sm me-2"
                            onClick={() => handleCreateMailbox(u.id)}
                          >
                            메일함 생성
                          </button>
                        )}

                        {useMailEnabled && u.maildb === 1 && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDeleteMailbox(u.id)}
                          >
                            메일함 삭제
                          </button>
                        )}
                        {useMailEnabled && u.maildb === 1 && u.sso_yn === 0 && (
                          <button
                            className="btn btn-warning btn-sm ms-2"
                            onClick={() => handleRegisterSso(u.userid)}
                          >
                            SSO 생성
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserList;